import { DateInput, TimeField } from "@/components/ui/baseUi/datefield-rac"
import { useDateTimeCalendarProps } from "@/modules/boxSlot/hooks/useDateTimeCalendarProps"
import type { BoxSlotTemplateGroupFormikEntity } from "@/modules/boxSlotTemplate/entities/boxSlotTemplateGroupFormikEntity"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import { type SimpleFormik } from "@/typings/formik"
import { Time } from "@internationalized/date"
import { type BoxSlotTemplateGroupInputKey } from "@modules/boxSlotTemplate/boxSlotTemplateGroupFormikTypes"
import dayjs from "dayjs"
import { ClockIcon } from "lucide-react"

export function SlotTemplateRange({
  index,
  formik,
  addNewRange: _addNewRange,
  displayAddRangeButton: _displayAddRangeButton,
  removeRange: _removeRange,
  displayLabels,
  className,
  ...props
}: {
  formik: SimpleFormik<BoxSlotTemplateGroupFormikEntity, BoxSlotTemplateGroupInputKey> & {
    setFieldTouched: (field: string, touched?: boolean, shouldValidate?: boolean) => void
  }
  removeRange?: (arg: number) => void
  index?: number
  addNewRange?: () => void
  displayAddRangeButton?: boolean
  displayLabels?: boolean
} & React.ComponentProps<"div">) {
  const { validateStartDateTime } = useDateTimeCalendarProps()

  const currentSlotTemplateRange =
    typeof index !== "undefined" ? formik.values.slotTemplatesRanges.find((range) => range.index === index) : null
  const isPlaceholder = typeof index === "undefined" || !currentSlotTemplateRange
  const itemIndex = formik.values.slotTemplatesRanges.findIndex((range) => range.index === index)

  if (!currentSlotTemplateRange && !isPlaceholder) return null

  return (
    <div className={cn("flex w-full flex-row items-center gap-4", className)} {...props}>
      <TimeField
        className="flex-1"
        aria-label={displayLabels ? word("programmation.slotTemplates.form.hour.startTime") : ""}
        shouldForceLeadingZeros={true}
        isDisabled={isPlaceholder}
        value={
          !isPlaceholder && currentSlotTemplateRange.startTime
            ? new Time(currentSlotTemplateRange.startTime?.getHours(), currentSlotTemplateRange.startTime?.getMinutes())
            : null
        }
        onChange={(e) => {
          if (e?.hour !== undefined && e?.minute !== undefined) {
            const newStartDateTime = dayjs()
              .set("hour", e.hour)
              .set("minute", e.minute)
              .set("second", 0)
              .set("millisecond", 0)
              .toDate()

            if (currentSlotTemplateRange) {
              validateStartDateTime(newStartDateTime, currentSlotTemplateRange.endTime, (val) => {
                formik.setFieldValue(`slotTemplatesRanges[${itemIndex}].endTime`, val)
              })
            }

            formik.setFieldValue(`slotTemplatesRanges[${itemIndex}].startTime`, newStartDateTime)
          }
        }}
        onBlur={() => {
          formik.setFieldTouched(`slotTemplatesRanges[${itemIndex}].startTime`, true)
        }}
      >
        <div className="relative">
          <div className="text-muted-foreground/80 pointer-events-none absolute inset-y-0 start-0 z-10 flex items-center justify-center ps-3">
            <ClockIcon size={16} aria-hidden="true" />
          </div>
          <DateInput className="ps-9" />
        </div>
      </TimeField>

      <TimeField
        className="flex-1"
        aria-label={displayLabels ? word("programmation.slotTemplates.form.hour.endTime") : ""}
        shouldForceLeadingZeros={true}
        isDisabled={isPlaceholder}
        value={
          !isPlaceholder && currentSlotTemplateRange.endTime
            ? new Time(currentSlotTemplateRange.endTime?.getHours(), currentSlotTemplateRange.endTime?.getMinutes())
            : null
        }
        onChange={(e) => {
          if (e?.hour !== undefined && e?.minute !== undefined) {
            const newEndDateTime = dayjs()
              .set("hour", e.hour)
              .set("minute", e.minute)
              .set("second", 0)
              .set("millisecond", 0)
              .toDate()

            formik.setFieldValue(`slotTemplatesRanges[${itemIndex}].endTime`, newEndDateTime)
          }
        }}
        onBlur={() => {
          formik.setFieldTouched(`slotTemplatesRanges[${itemIndex}].endTime`, true)
        }}
      >
        <div className="relative">
          <div className="text-muted-foreground/80 pointer-events-none absolute inset-y-0 start-0 z-10 flex items-center justify-center ps-3">
            <ClockIcon size={16} aria-hidden="true" />
          </div>
          <DateInput className="ps-9" />
        </div>
      </TimeField>
    </div>
  )
}
